/**
 * Table Export Utilities
 *
 * Provides functionality to export markdown tables from AI responses to CSV and Excel formats
 */

/**
 * Parse markdown table string into structured data
 * @param {string} tableMarkdown - Markdown table string
 * @returns {Object} { headers: string[], rows: string[][] }
 */
export function parseMarkdownTable(tableMarkdown) {
  const lines = tableMarkdown.trim().split('\n');

  if (lines.length < 2) {
    throw new Error('Invalid table format');
  }

  // Parse header row
  const headerLine = lines[0];
  const headers = headerLine
    .split('|')
    .map(cell => cell.trim())
    .filter(cell => cell !== '');

  // Skip separator row (line with ---)
  const dataLines = lines.slice(2); // Skip header and separator

  // Parse data rows
  const rows = dataLines.map(line => {
    return line
      .split('|')
      .map(cell => {
        // Clean markdown formatting from cells
        let cleaned = cell.trim();
        // Remove ** for bold
        cleaned = cleaned.replace(/\*\*/g, '');
        // Remove $ and commas for proper number formatting in Excel
        // Keep $ prefix for display but make numbers parseable
        return cleaned;
      })
      .filter(cell => cell !== '');
  });

  return { headers, rows };
}

/**
 * Convert table data to CSV format
 * @param {string[]} headers - Table headers
 * @param {string[][]} rows - Table rows
 * @returns {string} CSV string
 */
export function tableToCSV(headers, rows) {
  const escapeCsvValue = (value) => {
    // Escape double quotes and wrap in quotes if contains comma, quote, or newline
    const stringValue = String(value);
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const csvLines = [];

  // Add headers
  csvLines.push(headers.map(escapeCsvValue).join(','));

  // Add rows
  rows.forEach(row => {
    csvLines.push(row.map(escapeCsvValue).join(','));
  });

  return csvLines.join('\n');
}

/**
 * Convert table data to Excel-compatible HTML format
 * This creates an HTML table that Excel can open natively
 * @param {string[]} headers - Table headers
 * @param {string[][]} rows - Table rows
 * @returns {string} HTML string
 */
export function tableToExcelHTML(headers, rows) {
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    table { border-collapse: collapse; font-family: Arial, sans-serif; }
    th { background-color: #4a5568; color: white; padding: 8px; border: 1px solid #ccc; font-weight: bold; }
    td { padding: 8px; border: 1px solid #ccc; }
    tr:nth-child(even) { background-color: #f7fafc; }
    tr.total-row { background-color: #e6f2ff; font-weight: bold; }
  </style>
</head>
<body>
  <table>
    <thead>
      <tr>
        ${headers.map(h => `<th>${escapeHtml(h)}</th>`).join('')}
      </tr>
    </thead>
    <tbody>
      ${rows.map((row, idx) => {
        // Check if this is a TOTAL row (first cell contains "TOTAL")
        const isTotalRow = row[0] && row[0].toUpperCase().includes('TOTAL');
        const rowClass = isTotalRow ? ' class="total-row"' : '';
        return `<tr${rowClass}>
          ${row.map(cell => `<td>${escapeHtml(cell)}</td>`).join('')}
        </tr>`;
      }).join('')}
    </tbody>
  </table>
</body>
</html>
  `.trim();

  return html;
}

/**
 * Escape HTML special characters
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeHtml(text) {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return String(text).replace(/[&<>"']/g, m => map[m]);
}

/**
 * Download a file with given content and filename
 * @param {string} content - File content
 * @param {string} filename - Filename with extension
 * @param {string} mimeType - MIME type
 */
export function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export markdown table to CSV
 * @param {string} tableMarkdown - Markdown table string
 * @param {string} filename - Base filename (without extension)
 */
export function exportTableToCSV(tableMarkdown, filename = 'table') {
  try {
    const { headers, rows } = parseMarkdownTable(tableMarkdown);
    const csv = tableToCSV(headers, rows);
    downloadFile(csv, `${filename}.csv`, 'text/csv;charset=utf-8;');
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    throw new Error('Failed to export table to CSV');
  }
}

/**
 * Export markdown table to Excel (HTML format that Excel can open)
 * @param {string} tableMarkdown - Markdown table string
 * @param {string} filename - Base filename (without extension)
 */
export function exportTableToExcel(tableMarkdown, filename = 'table') {
  try {
    const { headers, rows } = parseMarkdownTable(tableMarkdown);
    const html = tableToExcelHTML(headers, rows);

    // Use .xls extension with HTML content - Excel will open it correctly
    downloadFile(html, `${filename}.xls`, 'application/vnd.ms-excel');
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error('Failed to export table to Excel');
  }
}

/**
 * Generate a filename based on table content and timestamp
 * @param {string} tableMarkdown - Markdown table string
 * @param {string} hotelName - Hotel name
 * @returns {string} Generated filename
 */
export function generateTableFilename(tableMarkdown, hotelName = 'Hotel') {
  const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  // Try to infer table type from content
  const content = tableMarkdown.toLowerCase();
  let tableType = 'data';

  if (content.includes('revenue')) tableType = 'revenue';
  else if (content.includes('occupancy')) tableType = 'occupancy';
  else if (content.includes('forecast') || content.includes('otb')) tableType = 'forecast';
  else if (content.includes('daily')) tableType = 'daily';
  else if (content.includes('mtd') || content.includes('month')) tableType = 'monthly';
  else if (content.includes('ytd') || content.includes('year')) tableType = 'yearly';

  // Clean hotel name for filename
  const cleanHotelName = hotelName.replace(/[^a-z0-9]/gi, '_').substring(0, 30);

  return `${cleanHotelName}_${tableType}_${timestamp}`;
}
